// src/components/plan/ProgressDetailModal.jsx
import React from 'react';
import { Building2, CheckCircle, CalendarDays, Loader2, User } from 'lucide-react';
import { clsx } from 'clsx';

export function ProgressDetailModal({ selectedBiz, onClose, detailList, detailLoading }) {
  if (!selectedBiz) return null;

  // 🌟 쿼리에서 추가된 bizName과 staffNm을 데이터에서 꺼냄 (데이터가 없을 땐 부모 데이터 사용)
  const displayBizName = detailList.length > 0 ? detailList[0].bizName : selectedBiz.companyName;
  const displayStaffNm = detailList.length > 0 ? detailList[0].staffNm : selectedBiz.staffNm;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
        
        {/* 모달 헤더 */}
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <div className="flex flex-col">
            <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" /> {displayBizName} 
            </h3>
            <span className="text-sm font-medium text-slate-500 flex items-center gap-1 mt-1">
              <User className="w-4 h-4" /> 배정 담당자: <span className="text-indigo-600">{displayStaffNm}</span>
            </span>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:bg-slate-200 hover:text-slate-700 rounded-lg transition-colors">✕</button>
        </div>
        
        {/* 모달 본문 (타임라인) */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {detailLoading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600"/>
              <p className="text-slate-500 text-sm">상세 일정을 불러오는 중입니다...</p>
            </div>
          ) : detailList.length === 0 ? (
            <div className="text-center py-10 text-slate-500 bg-slate-50 rounded-xl border border-slate-100">조회된 배정 회차가 없습니다.</div>
          ) : (
            <div className="space-y-6 pl-2">
              {detailList.map((step, idx) => {
                
                // 🌟 추가: 해당 회차의 일정이 지연되었는지 프론트엔드에서 날짜 비교로 확인
                const isDelayed = !step.executeDtm && step.visitDate && new Date(step.visitDate) < new Date(new Date().setHours(0,0,0,0));

                return (
                  <div key={idx} className="flex gap-5 relative">
                    {/* 타임라인 연결선 */}
                    {idx !== detailList.length - 1 && <div className="absolute left-[11px] top-8 bottom-[-24px] w-[2px] bg-slate-200"></div>}
                    
                    {/* 타임라인 노드 아이콘 */}
                    <div className={clsx("w-6 h-6 rounded-full flex items-center justify-center shrink-0 z-10 ring-4 ring-white shadow-sm mt-1", 
                      step.atchFileId ? "bg-green-500 text-white" : 
                      step.executeDtm ? "bg-indigo-500 text-white" : 
                      isDelayed ? "bg-orange-500 text-white" : "bg-slate-200 text-slate-500" // 🌟 지연시 주황색 아이콘
                    )}>
                      {step.atchFileId ? <CheckCircle className="w-3.5 h-3.5" /> : <span className="text-xs font-bold">{step.visitRound}</span>}
                    </div>

                    {/* 회차 상세 카드 */}
                    <div className="flex-1 bg-white hover:bg-slate-50 transition-colors rounded-xl p-4 border border-slate-200 shadow-sm">
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="font-bold text-slate-800 text-base">{step.visitRound}차 컨설팅 진행 현황</h4>
                        <span className="text-xs font-semibold text-slate-500 flex items-center gap-1 bg-slate-100 px-2 py-1 rounded-md">
                          <CalendarDays className="w-3.5 h-3.5"/> 예정: {step.visitDate?.substring(0,10)}
                        </span>
                      </div>
                      <div className="flex gap-2 text-sm font-medium">
                         {/* 🌟 방문 실시 여부에 지연 조건 추가 */}
                         <span className={clsx("px-2.5 py-1.5 rounded-lg border", 
                            step.executeDtm ? "bg-indigo-50 text-indigo-700 border-indigo-100" : 
                            isDelayed ? "bg-orange-50 text-orange-700 border-orange-100" : 
                            "bg-slate-50 text-slate-500 border-slate-200"
                         )}>
                           방문실시: {step.executeDtm ? step.executeDtm.substring(0,10) : isDelayed ? '일정 지연' : '미실시'}
                         </span>
                         
                         <span className={clsx("px-2.5 py-1.5 rounded-lg border", step.atchFileId ? "bg-green-50 text-green-700 border-green-100" : "bg-rose-50 text-rose-600 border-rose-100")}>
                           산출물: {step.atchFileId ? '등록 완료' : '미등록'}
                         </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 모달 푸터 */}
        <div className="p-4 border-t border-slate-100 flex justify-end bg-slate-50">
          <button onClick={onClose} className="px-5 py-2.5 bg-slate-700 text-white rounded-xl hover:bg-slate-800 text-sm font-bold transition-colors shadow-sm">닫기</button>
        </div>
      </div>
    </div>
  );
}